package com.example.monetix

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import app.cash.sqldelight.async.coroutines.awaitAsList
import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.example.monetix.db.*
import com.example.monetix.utils.AutomationEngine
import com.example.monetix.utils.PaymentEngine
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import dev.gitlive.firebase.firestore.firestore

class HomeViewModel(
    private val repository: MonetixRepository,
    private val db: MonetixDatabase
) : ViewModel() {

    val firestore = FirestoreHelper()

    // 🚀 LUCCHETTO PRO: Impedisce i doppi click
    private val processingSet = mutableSetOf<String>()

    val accounts: StateFlow<List<AccountEntity>> = db.accountQueries.getAllAccounts().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val transactions: StateFlow<List<TransactionEntity>> = db.transactionQueries.getAllTransactions().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val categories: StateFlow<List<CategoryEntity>> = db.categoryQueries.getAllCategories().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val recurringItems: StateFlow<List<RecurringExpenseEntity>> = db.recurringExpenseQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val financings: StateFlow<List<FinancingEntity>> = db.financingQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val favorites: StateFlow<List<FavoriteTemplateEntity>> = db.favoriteTemplateQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val savingsGoals: StateFlow<List<SavingsGoalEntity>> = db.savingsGoalQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val budgets: StateFlow<List<BudgetEntity>> = db.budgetQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())
    val piggyBanks: StateFlow<List<PiggyBankEntity>> = db.piggyBankQueries.all().asFlow().mapToList(Dispatchers.Default).map { list -> list.filter { !it.isDeleted } }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyList())

    val accountingBalancesMap: StateFlow<Map<String, Long>> = combine(accounts, transactions) { accs, txList ->
        accs.associate { acc ->
            val validTxs = txList.filter { !it.isDeleted }
            val inc = validTxs.filter { it.accountId == acc.id && it.type == "INCOME" }.sumOf { it.amountCents }
            val exp = validTxs.filter { it.accountId == acc.id && it.type == "EXPENSE" }.sumOf { it.amountCents }
            val transIn = validTxs.filter { it.toAccountId == acc.id && it.type == "TRANSFER" }.sumOf { it.amountCents }
            val transOut = validTxs.filter { it.fromAccountId == acc.id && it.type == "TRANSFER" }.sumOf { it.amountCents }
            acc.id to (inc - exp + transIn - transOut)
        }
    }.stateIn(viewModelScope, SharingStarted.Eagerly, emptyMap())

    val globalBalanceCents: StateFlow<Long> = accountingBalancesMap.map { it.values.sum() }
        .stateIn(viewModelScope, SharingStarted.Eagerly, 0L)

    private val _bankAdjustmentCents = kotlinx.coroutines.flow.MutableStateFlow(0L)
    val bankAdjustmentCents: StateFlow<Long> = _bankAdjustmentCents

    init {
        sincronizzaTutto()
    }

    fun impostaUtente(uid: String) {
        firestore.sessionUid = uid

        viewModelScope.launch {
            try {
                val docRef = Firebase.firestore.collection("users").document(uid)
                val snapshot = docRef.get()

                if (snapshot.exists) {
                    val value = try {
                        snapshot.get<Long>("bankAdjustmentCents")
                    } catch (e: Exception) {
                        try {
                            snapshot.get<Double>("bankAdjustmentCents").toLong()
                        } catch (e2: Exception) {
                            0L
                        }
                    }

                    _bankAdjustmentCents.value = value
                }
            } catch (e: Exception) {
                println("🔥 MONETIX_DEBUG: [ERRORE DOWNLOAD] ${e.message}")
            }
        }
    }

    private fun findRelatedGoal(tx: TransactionEntity, goals: List<SavingsGoalEntity>): SavingsGoalEntity? {
        try {
            // 🚀 FIX BLINDATO: Il postino ora cerca SOLO l'ID esatto (codice a barre) del Sogno, PAC o Salvadanaio
            val tagRegex = Regex("\\[(?:sav|pac_sav|pb):([^\\]]+)\\]")
            val match = tagRegex.find(tx.merchantKey ?: "") ?: tagRegex.find(tx.description ?: "")

            if (match != null) {
                val idToFind = match.groupValues[1].trim()
                return goals.find { it.id == idToFind }
            }

            // Fallback d'emergenza solo per vecchissime transazioni senza tag
            return goals.find { goal ->
                val gName = goal.name.replace(Regex("\\[(PAC|ACC|DYN):.*?\\]"), "").trim()
                if (gName.isEmpty()) return@find false
                tx.categoryId == goal.id || (tx.merchantKey?.contains(gName, ignoreCase = true) == true) || (tx.description?.contains(gName, ignoreCase = true) == true)
            }
        } catch (e: Exception) { return null }
    }

    private var syncJob: Job? = null

    private val _isInitialSyncComplete = kotlinx.coroutines.flow.MutableStateFlow(false)
    val isInitialSyncComplete: StateFlow<Boolean> = _isInitialSyncComplete

    fun sincronizzaTutto() {
        if (syncJob?.isActive == true) return
        syncJob = viewModelScope.launch {
            var uid = firestore.sessionUid ?: Firebase.auth.currentUser?.uid
            while (uid == null) { delay(500); uid = firestore.sessionUid ?: Firebase.auth.currentUser?.uid }

            launch {
                firestore.scaricaConti().catch {}.collect { cloudAccs ->
                    try {
                        val cloudIds = cloudAccs.map { it.id }.toSet()
                        val localAccs = try { db.accountQueries.getAllAccounts().awaitAsList() } catch(e: Exception) { accounts.value }
                        localAccs.forEach { localAcc ->
                            if (localAcc.id !in cloudIds && !localAcc.isDeleted) {
                                try { db.accountQueries.upsert(localAcc.id, localAcc.name, localAcc.logoEmoji, localAcc.logoUri, localAcc.isVisible, localAcc.isHidden, localAcc.isSavings, localAcc.isArchived, true, localAcc.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) } catch (e: Exception) {}
                            }
                        }
                        cloudAccs.forEach { try { db.accountQueries.upsert(it.id, it.name, it.logoEmoji, it.logoUri, it.isVisible, it.isHidden, it.isSavings, it.isArchived, it.isDeleted, it.createdAtEpochMs, it.updatedAtEpochMs) } catch (e: Exception) {} }
                    } catch (e: Exception) {}
                }
            }
            launch { 
                firestore.scaricaMovimenti().catch {}.collect { cloudTxs -> 
                    try { 
                        val cloudIds = cloudTxs.map { it.id }.toSet()
                        val localTxs = try { db.transactionQueries.getAllTransactions().awaitAsList() } catch(e: Exception) { transactions.value }
                        
                        localTxs.forEach { localTx -> 
                            if (localTx.id !in cloudIds && !localTx.isDeleted) { 
                                try { db.transactionQueries.softDelete(Clock.System.now().toEpochMilliseconds(), localTx.id) } catch (e: Exception) {} 
                            } 
                        }
                        cloudTxs.forEach { 
                            try { db.transactionQueries.upsert(it.id, it.accountId, it.toAccountId, it.fromAccountId, it.amountCents, it.type, it.dateEpochDay, it.categoryId, it.description, it.merchantKey, it.subcategoryText, it.logoEmoji, it.logoUri, it.isHidden, it.isDeleted, it.reminderDays, it.createdAtEpochMs, it.updatedAtEpochMs) } catch (e: Exception) {} 
                        }
                    } catch (e: Exception) {} 
                } 
            }

            launch {
                firestore.scaricaCategorie().catch {}.collect { cloudCats ->
                    try {
                        val cloudIds = cloudCats.map { it.id }.toSet()
                        val localCats = try { db.categoryQueries.getAllCategories().awaitAsList() } catch(e: Exception) { categories.value }
                        localCats.forEach { localCat -> if (localCat.id !in cloudIds && !localCat.isDeleted) { try { db.categoryQueries.upsert(localCat.id, localCat.name, true, localCat.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) } catch (e: Exception) {} } }
    
                        if (cloudCats.isNotEmpty()) {
                            cloudCats.forEach { try { db.categoryQueries.upsert(it.id, it.name, it.isDeleted, it.createdAtEpochMs, it.updatedAtEpochMs) } catch (e: Exception) {} }
                        } else {
                            val defaultCategories = listOf("💰" to "Stipendio", "🛒" to "Spesa", "🚗" to "Auto & Trasporti", "🏠" to "Casa & Bollette", "🍕" to "Ristoranti & Svago", "🏥" to "Salute")
                            val now = Clock.System.now().toEpochMilliseconds()
                            defaultCategories.forEachIndexed { index, (emoji, name) ->
                                val cat = CategoryEntity("cat_${index}_$now", "$emoji $name", false, now, now)
                                try { db.categoryQueries.upsert(cat.id, cat.name, cat.isDeleted, cat.createdAtEpochMs, cat.updatedAtEpochMs) } catch (e: Exception) {}
                                try { firestore.salva("categorie", cat.id, cat) } catch (e: Exception) {}
                            }
                        }
                    } catch (e: Exception) {}
                }
            }

            launch {
                firestore.scaricaRicorrenti().catch {}.collect { cloudRecs ->
                    try {
                        val cloudIds = cloudRecs.map { it.id }.toSet()
                        val localRecs = try { db.recurringExpenseQueries.all().awaitAsList() } catch(e: Exception) { recurringItems.value }
                        localRecs.forEach { localRec ->
                            if (localRec.id !in cloudIds && !localRec.isDeleted) {
                                try { db.recurringExpenseQueries.upsert(localRec.id, localRec.name, localRec.type, localRec.amountCents, localRec.accountId, localRec.toAccountId, localRec.categoryId, localRec.dayOfMonth, localRec.isEnabled, true, localRec.cycleTarget, localRec.logoEmoji, localRec.logoUri, localRec.notes, localRec.autoGenerateOnReminder, localRec.nextEpochDay, localRec.frequency, localRec.isPaused, localRec.startEpochDay, localRec.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) } catch (e: Exception) {}
                            }
                        }
                        cloudRecs.forEach { item -> try { db.recurringExpenseQueries.upsert(item.id, item.name, item.type, item.amountCents, item.accountId, item.toAccountId, item.categoryId, item.dayOfMonth, item.isEnabled, item.isDeleted, item.cycleTarget, item.logoEmoji, item.logoUri, item.notes, item.autoGenerateOnReminder, item.nextEpochDay, item.frequency, item.isPaused, item.startEpochDay, item.createdAtEpochMs, item.updatedAtEpochMs) } catch (e: Exception) {} }
                    } catch (e: Exception) {}
                }
            }
            launch { 
                firestore.scaricaDebiti().catch {}.collect { cloudFins -> 
                    try { 
                        val cloudIds = cloudFins.map { it.id }.toSet()
                        val localFins = try { db.financingQueries.all().awaitAsList() } catch(e: Exception) { financings.value }
                        localFins.forEach { localFin -> if (localFin.id !in cloudIds && !localFin.isClosed) { try { db.financingQueries.setClosed(isClosed = true, updatedAtEpochMs = Clock.System.now().toEpochMilliseconds(), id = localFin.id) } catch (e: Exception) {} } }
                        cloudFins.forEach { fin -> try { db.financingQueries.upsert(fin.id, fin.name, fin.creditorName, fin.purpose, fin.installmentAmountCents, fin.commissionCents, fin.extraInstallmentCents, fin.extraInstallmentMonth, fin.totalAmountCents, fin.remainingAmountCents, fin.accountId, fin.categoryId, fin.startEpochDay, fin.endEpochDay, fin.autoGenerateOnReminder, fin.isClosed, fin.isDeleted, fin.logoEmoji, fin.logoUri, fin.createdAtEpochMs, fin.updatedAtEpochMs) } catch (e: Exception) {} } 
                    } catch (e: Exception) {} 
                } 
            }
            
            launch { 
                firestore.scaricaPreferiti().catch {}.collect { cloudFavs -> 
                    cloudFavs.forEach { p -> try { db.favoriteTemplateQueries.upsert(p.id, p.description, p.amountCents, p.categoryId, p.logoEmoji, p.logoUri, p.isDeleted, p.createdAtEpochMs, p.updatedAtEpochMs) } catch (e: Exception) {} } 
                } 
            }
            
            launch { 
                firestore.scaricaRisparmi().catch {}.collect { cloudSavings -> 
                    try { 
                        val cloudIds = cloudSavings.map { it.id }.toSet()
                        val localGoals = try { db.savingsGoalQueries.all().awaitAsList() } catch(e: Exception) { savingsGoals.value }
                        localGoals.forEach { localGoal -> if (localGoal.id !in cloudIds && !localGoal.isDeleted) { try { db.savingsGoalQueries.softDelete(Clock.System.now().toEpochMilliseconds(), localGoal.id) } catch (e: Exception) {} } }
                        cloudSavings.forEach { s -> try { db.savingsGoalQueries.upsert(s.id, s.name, s.targetAmountCents, s.savedAmountCents, s.emoji ?: "", s.imageUri ?: "", s.isCompleted, s.isDeleted, s.createdAtEpochMs, s.updatedAtEpochMs) } catch (e: Exception) {} } 
                    } catch (e: Exception) {} 
                } 
            }
            
            launch { 
                firestore.scaricaBudgets().catch {}.collect { cloudBudgets -> 
                    cloudBudgets.forEach { b -> try { db.budgetQueries.upsert(b.categoryId, b.limitCents, b.isDeleted, b.createdAtEpochMs, b.updatedAtEpochMs) } catch (e: Exception) {} } 
                } 
            }
            
            launch { 
                firestore.scaricaSalvadanai().catch {}.collect { cloudPiggyBanks -> 
                    try { 
                        val cloudIds = cloudPiggyBanks.map { it.id }.toSet()
                        val localBanks = try { db.piggyBankQueries.all().awaitAsList() } catch(e: Exception) { piggyBanks.value }
                        localBanks.forEach { localBank -> if (localBank.id !in cloudIds && !localBank.isDeleted) { try { db.piggyBankQueries.deleteById(Clock.System.now().toEpochMilliseconds(), localBank.id) } catch (e: Exception) {} } }
                        cloudPiggyBanks.forEach { pb -> try { db.piggyBankQueries.upsert(pb.id, pb.name, pb.accumulatedCents, pb.emoji, pb.colorHex, pb.imageUri, pb.isDeleted, pb.createdAtEpochMs, pb.updatedAtEpochMs) } catch (e: Exception) {} } 
                    } catch (e: Exception) {} 
                } 
            }

            // ASCOLTA LE PREFERENZE DAL CLOUD E AGGIORNA LA UI
            launch {
                firestore.getPreferenze().catch {}.collect { prefs ->
                    if (prefs != null) {
                        val cloudAdjustment = (prefs.bankAdjustmentCents ?: 0.0).toLong()
                        _bankAdjustmentCents.value = cloudAdjustment
                    }
                }
            }

            launch {
                delay(800)
                _isInitialSyncComplete.value = true
            }
        }
    }

    fun runStartupTasks(db: MonetixDatabase, firestore: FirestoreHelper) {
        viewModelScope.launch {
            try {
                AutomationEngine.processPendingAutomations(db, firestore) // 👈 Pulito
            } catch (e: Exception) {}
        }
    }
    fun nukeAllData() { viewModelScope.launch { try { firestore.distruggiTuttoIlCloud() } catch (e: Exception) {} } }

    fun saveCategory(category: CategoryEntity) {
        viewModelScope.launch {
            try { db.categoryQueries.upsert(category.id, category.name, category.isDeleted, category.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) } catch (e: Exception) {}
            try { firestore.salva("categorie", category.id, category) } catch (e: Exception) {}
        }
    }

    fun deleteCategory(categoryId: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allCats = try { db.categoryQueries.getAllCategories().awaitAsList() } catch(e: Exception) { categories.value }
                val cat = allCats.find { it.id == categoryId } ?: return@launch
                if (cat.isDeleted) return@launch
                val deletedCat = CategoryEntity(cat.id, cat.name, true, cat.createdAtEpochMs, now)
                try { db.categoryQueries.upsert(deletedCat.id, deletedCat.name, true, deletedCat.createdAtEpochMs, now) } catch (e: Exception) {}
                try { firestore.salva("categorie", categoryId, deletedCat) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun saveTransaction(tx: TransactionEntity) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            var goalToSync: SavingsGoalEntity? = null
            var bankToSync: PiggyBankEntity? = null // 🚀 IL FIX: ORA GESTISCE ANCHE I SALVADANAI!

            try {
                if (tx.categoryId == "cat_savings_auto") {
                    val catExists = categories.value.any { it.id == "cat_savings_auto" }
                    if (!catExists) {
                        val newCat = CategoryEntity("cat_savings_auto", "🐖 Risparmi", false, now, now)
                        db.categoryQueries.upsert(newCat.id, newCat.name, newCat.isDeleted, newCat.createdAtEpochMs, newCat.updatedAtEpochMs)
                        firestore.salva("categorie", newCat.id, newCat)
                    }
                }

                val allTxs = try { db.transactionQueries.getAllTransactions().awaitAsList() } catch(e: Exception) { transactions.value }
                val oldTx = allTxs.find { it.id == tx.id }

                db.transactionQueries.upsert(tx.id, tx.accountId, tx.toAccountId, tx.fromAccountId, tx.amountCents, tx.type, tx.dateEpochDay, tx.categoryId, tx.description, tx.merchantKey, tx.subcategoryText, tx.logoEmoji, tx.logoUri, tx.isHidden, tx.isDeleted, tx.reminderDays, tx.createdAtEpochMs, tx.updatedAtEpochMs)

                // 🚀 FUNZIONE SUPREMA PER CALCOLARE L'IMPATTO MATEMATICO (Versamento vs Prelievo)
                val getNetEffect = { t: TransactionEntity ->
                    when {
                        t.type == "INCOME" -> -t.amountCents // Sblocco: toglie
                        t.type == "EXPENSE" && t.amountCents < 0 -> t.amountCents // Prelievo negativo: toglie
                        t.type == "TRANSFER" && t.fromAccountId == "salvadanaio_virtuale" -> -t.amountCents // Uscita
                        else -> t.amountCents // Versamento standard: aggiunge
                    }
                }

                val oldEffect = if (oldTx == null) 0L else getNetEffect(oldTx)
                val newEffect = getNetEffect(tx)
                val amountToAdd = newEffect - oldEffect

                // --- 1. AGGIORNAMENTO AUTOMATICO SOGNI E PAC ---
                val allGoals = try { db.savingsGoalQueries.all().awaitAsList() } catch(e: Exception) { savingsGoals.value }
                val relatedGoal = findRelatedGoal(tx, allGoals)

                if (relatedGoal != null && amountToAdd != 0L) {
                    val newAmount = (relatedGoal.savedAmountCents + amountToAdd).coerceAtLeast(0L)
                    val isComp = if (relatedGoal.targetAmountCents > 0) newAmount >= relatedGoal.targetAmountCents else false
                    goalToSync = SavingsGoalEntity(relatedGoal.id, relatedGoal.name, relatedGoal.targetAmountCents, newAmount, relatedGoal.emoji, relatedGoal.imageUri, isComp, relatedGoal.isDeleted, relatedGoal.createdAtEpochMs, now)
                    db.savingsGoalQueries.upsert(goalToSync.id, goalToSync.name, goalToSync.targetAmountCents, goalToSync.savedAmountCents, goalToSync.emoji ?: "", goalToSync.imageUri ?: "", goalToSync.isCompleted, goalToSync.isDeleted, goalToSync.createdAtEpochMs, goalToSync.updatedAtEpochMs)
                }

                // --- 2. AGGIORNAMENTO AUTOMATICO SALVADANAI (IL FIX MANCANTE!) ---
                val pbRegex = Regex("\\[pb:([^\\]]+)\\]")
                val pbId = pbRegex.find(tx.merchantKey ?: "")?.groupValues?.get(1) ?: pbRegex.find(tx.description ?: "")?.groupValues?.get(1)

                if (!pbId.isNullOrBlank() && amountToAdd != 0L) {
                    val allBanks = try { db.piggyBankQueries.all().awaitAsList() } catch(e: Exception) { piggyBanks.value }
                    val relatedBank = allBanks.find { it.id == pbId.trim() }

                    if (relatedBank != null) {
                        val newAmount = (relatedBank.accumulatedCents + amountToAdd).coerceAtLeast(0L)
                        bankToSync = relatedBank.copy(accumulatedCents = newAmount, updatedAtEpochMs = now)
                        db.piggyBankQueries.upsert(bankToSync.id, bankToSync.name, bankToSync.accumulatedCents, bankToSync.emoji, bankToSync.colorHex, bankToSync.imageUri, bankToSync.isDeleted, bankToSync.createdAtEpochMs, bankToSync.updatedAtEpochMs)
                    }
                }

            } catch (e: Exception) {}

            try { firestore.salva("movimenti", tx.id, tx) } catch (e: Exception) {}
            if (goalToSync != null) { try { firestore.salva("risparmi", goalToSync.id, goalToSync) } catch (e: Exception) {} }
            // 🚀 SALVA IL SALVADANAIO ANCHE SUL CLOUD!
            if (bankToSync != null) { try { firestore.salva("salvadanai", bankToSync.id, bankToSync) } catch (e: Exception) {} }
        }
    }

    fun deleteTransaction(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            var goalToSync: SavingsGoalEntity? = null
            var bankToSync: PiggyBankEntity? = null
            var deletedTx: TransactionEntity? = null

            try {
                val allTxs = try { db.transactionQueries.getAllTransactions().awaitAsList() } catch (e: Exception) { transactions.value }
                val tx = allTxs.find { it.id == id } ?: return@launch
                if (tx.isDeleted) return@launch

                // 1. 🚀 TROVIAMO TUTTI I FIGLI (Contanti, Sotto-spese, ma ora ANCHE i movimenti dello SCUDO e SALVADANAI)
                val parentTag = "[p:$id]"
                val cashTag = "[cash:$id]"

                val children = allTxs.filter {
                    val isChild = !it.isDeleted && it.id != id && (
                            it.id == "tx_refill_$id" ||   // 🚀 Auto-Ricarica Scudo
                                    it.id == "tx_drain_$id" ||    // 🚀 Auto-Prelievo Scudo
                                    it.id == "tx_save_$id" ||     // 🚀 Accantonamento automatico
                                    it.id == "fee_$id" ||         // 🚀 Commissione
                                    it.description?.contains(parentTag) == true ||
                                    it.merchantKey?.contains(parentTag) == true ||
                                    it.description?.contains(cashTag) == true ||
                                    it.merchantKey?.contains(cashTag) == true
                            )
                    isChild
                }

                // 2. 🚀 ELIMINIAMO I FIGLI IN CASCATA PRIMA DEL PADRE
                children.forEach { child -> deleteTransaction(child.id) }

                // 3. OPERAZIONI SUL PADRE
                deletedTx = TransactionEntity(tx.id, tx.accountId, tx.toAccountId, tx.fromAccountId, tx.amountCents, tx.type, tx.dateEpochDay, tx.categoryId, tx.description, tx.merchantKey, tx.subcategoryText, tx.logoEmoji, tx.logoUri, tx.isHidden, true, tx.reminderDays, tx.createdAtEpochMs, now)
                db.transactionQueries.softDelete(now, id)

                // 🚀 MATEMATICA UNIVERSALE: Calcoliamo l'impatto esatto come nel salvataggio
                val getNetEffect = { t: TransactionEntity ->
                    when {
                        t.type == "INCOME" -> -t.amountCents
                        t.type == "EXPENSE" && t.amountCents < 0 -> t.amountCents
                        t.type == "TRANSFER" && t.fromAccountId == "salvadanaio_virtuale" -> -t.amountCents
                        else -> t.amountCents
                    }
                }

                // Aggiornamento SavingsGoal
                val allGoals = try { db.savingsGoalQueries.all().awaitAsList() } catch (e: Exception) { savingsGoals.value }
                val relatedGoal = findRelatedGoal(tx, allGoals)
                if (relatedGoal != null) {
                    val effect = getNetEffect(tx)
                    // 🚀 FIX: SOTTRAIAMO l'effetto, visto che stiamo ELIMINANDO!
                    val updatedCents = (relatedGoal.savedAmountCents - effect).coerceAtLeast(0L)

                    val isComp = if (relatedGoal.targetAmountCents > 0) updatedCents >= relatedGoal.targetAmountCents else false
                    goalToSync = SavingsGoalEntity(relatedGoal.id, relatedGoal.name, relatedGoal.targetAmountCents, updatedCents, relatedGoal.emoji, relatedGoal.imageUri, isComp, relatedGoal.isDeleted, relatedGoal.createdAtEpochMs, now)
                    db.savingsGoalQueries.upsert(goalToSync.id, goalToSync.name, goalToSync.targetAmountCents, goalToSync.savedAmountCents, goalToSync.emoji ?: "", goalToSync.imageUri ?: "", goalToSync.isCompleted, goalToSync.isDeleted, goalToSync.createdAtEpochMs, goalToSync.updatedAtEpochMs)
                }

                val pbRegex = Regex("\\[pb:([^\\]]+)\\]")

            } catch (e: Exception) { e.printStackTrace() }

            kotlinx.coroutines.delay(300)

            // Sincronizzazione Cloud
            if (deletedTx != null) {
                try {
                    firestore.elimina("movimenti", id)
                } catch (e: Exception) {
                    try { firestore.salva("movimenti", id, deletedTx) } catch (e: Exception) {}
                }
            }

            if (goalToSync != null) { try { firestore.salva("risparmi", goalToSync.id, goalToSync) } catch (e: Exception) {} }
            if (bankToSync != null) { try { firestore.salva("salvadanai", bankToSync.id, bankToSync) } catch (e: Exception) {} }
        }
    }

    fun deleteMultipleTransactions(txsToDelete: List<TransactionEntity>) { viewModelScope.launch { txsToDelete.forEach { tx -> deleteTransaction(tx.id) } } }

    fun saveSavingsGoal(goal: SavingsGoalEntity) {
        viewModelScope.launch {
            try {
                // 1. Salva nel DB Locale l'obiettivo (inizialmente a 0€)
                db.savingsGoalQueries.upsert(goal.id, goal.name, goal.targetAmountCents, goal.savedAmountCents, goal.emoji ?: "", goal.imageUri ?: "", goal.isCompleted, goal.isDeleted, goal.createdAtEpochMs, Clock.System.now().toEpochMilliseconds())

                // 2. Salva SUBITO nel Cloud l'obiettivo iniziale (a 0€)
                try { firestore.salva("risparmi", goal.id, goal) } catch (e: Exception) {}

                // 3. ORA E SOLO ORA facciamo partire il motore.
                // Se il motore aggiunge soldi, farà lui un NUOVO salvataggio su DB e Cloud con l'importo aggiornato,
                // sovrascrivendo correttamente lo 0 e diventando lui l'ultimo a parlare!
                if (goal.name.contains("[PAC:")) {
                    kotlinx.coroutines.delay(500)
                    com.example.monetix.utils.AutomationEngine.processPendingAutomations(db, firestore) // 👈 Pulito
                }

            } catch (e: Exception) {}
        }
    }

    fun deleteSavingsGoal(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allGoals = try { db.savingsGoalQueries.all().awaitAsList() } catch(e: Exception) { savingsGoals.value }
                val goal = allGoals.find { it.id == id } ?: return@launch
                if (goal.isDeleted) return@launch
                val deletedGoal = SavingsGoalEntity(goal.id, goal.name, goal.targetAmountCents, goal.savedAmountCents, goal.emoji, goal.imageUri, goal.isCompleted, true, goal.createdAtEpochMs, now)

                try { db.savingsGoalQueries.upsert(deletedGoal.id, deletedGoal.name, deletedGoal.targetAmountCents, deletedGoal.savedAmountCents, deletedGoal.emoji ?: "", deletedGoal.imageUri ?: "", deletedGoal.isCompleted, deletedGoal.isDeleted, deletedGoal.createdAtEpochMs, deletedGoal.updatedAtEpochMs) } catch (e: Exception) {}
                try { firestore.salva("risparmi", id, deletedGoal) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun savePiggyBank(bank: PiggyBankEntity) {
        viewModelScope.launch {
            try {
                db.piggyBankQueries.upsert(bank.id, bank.name, bank.accumulatedCents, bank.emoji, bank.colorHex, bank.imageUri, bank.isDeleted, bank.createdAtEpochMs, Clock.System.now().toEpochMilliseconds())
            } catch (e: Exception) {}
            try { firestore.salva("salvadanai", bank.id, bank) } catch (e: Exception) {}
        }
    }

    fun deletePiggyBank(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allBanks = try { db.piggyBankQueries.all().awaitAsList() } catch(e: Exception) { piggyBanks.value }
                val bank = allBanks.find { it.id == id } ?: return@launch
                if (bank.isDeleted) return@launch
                val deletedBank = bank.copy(isDeleted = true, updatedAtEpochMs = now)

                try { db.piggyBankQueries.deleteById(now, id) } catch (e: Exception) {}
                try { firestore.salva("salvadanai", id, deletedBank) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun saveBudget(budget: BudgetEntity) {
        viewModelScope.launch {
            try { db.budgetQueries.upsert(budget.categoryId, budget.limitCents, budget.isDeleted, budget.createdAtEpochMs, Clock.System.now().toEpochMilliseconds()) } catch (e: Exception) {}
            try { firestore.salva("budgets", budget.categoryId, budget) } catch (e: Exception) {}
        }
    }

    fun saveFinancingWithTransactions(fin: FinancingEntity, txs: List<TransactionEntity>) {
        viewModelScope.launch {
            try {
                txs.forEach { tx -> db.transactionQueries.upsert(tx.id, tx.accountId, tx.toAccountId, tx.fromAccountId, tx.amountCents, tx.type, tx.dateEpochDay, tx.categoryId, tx.description, tx.merchantKey, tx.subcategoryText, tx.logoEmoji, tx.logoUri, tx.isHidden, tx.isDeleted, tx.reminderDays, tx.createdAtEpochMs, tx.updatedAtEpochMs) }

                db.financingQueries.upsert(
                    fin.id, fin.name, fin.creditorName, fin.purpose,
                    fin.installmentAmountCents, fin.commissionCents, fin.extraInstallmentCents, fin.extraInstallmentMonth,
                    fin.totalAmountCents, fin.remainingAmountCents, fin.accountId, fin.categoryId,
                    fin.startEpochDay, fin.endEpochDay, fin.autoGenerateOnReminder, fin.isClosed,
                    fin.isDeleted, fin.logoEmoji, fin.logoUri, fin.createdAtEpochMs,
                    Clock.System.now().toEpochMilliseconds()
                )

                AutomationEngine.processPendingAutomations(db, firestore)
            } catch (e: Exception) {}

            txs.forEach { tx -> try { firestore.salva("movimenti", tx.id, tx) } catch (e: Exception) {} }
            try { firestore.salva("debiti", fin.id, fin) } catch (e: Exception) {}
        }
    }

    fun saveMultipleTransactions(txs: List<TransactionEntity>) {
        viewModelScope.launch {
            try { txs.forEach { tx -> db.transactionQueries.upsert(tx.id, tx.accountId, tx.toAccountId, tx.fromAccountId, tx.amountCents, tx.type, tx.dateEpochDay, tx.categoryId, tx.description, tx.merchantKey, tx.subcategoryText, tx.logoEmoji, tx.logoUri, tx.isHidden, tx.isDeleted, tx.reminderDays, tx.createdAtEpochMs, tx.updatedAtEpochMs) } } catch (e: Exception) {}
            txs.forEach { tx -> try { firestore.salva("movimenti", tx.id, tx) } catch (e: Exception) {} }
        }
    }

    fun saveFinancing(fin: FinancingEntity) {
        viewModelScope.launch {
            try {
                db.financingQueries.upsert(
                    fin.id, fin.name, fin.creditorName, fin.purpose,
                    fin.installmentAmountCents, fin.commissionCents, fin.extraInstallmentCents, fin.extraInstallmentMonth,
                    fin.totalAmountCents, fin.remainingAmountCents, fin.accountId, fin.categoryId,
                    fin.startEpochDay, fin.endEpochDay, fin.autoGenerateOnReminder, fin.isClosed,
                    fin.isDeleted, fin.logoEmoji, fin.logoUri, fin.createdAtEpochMs,
                    Clock.System.now().toEpochMilliseconds()
                )
                AutomationEngine.processPendingAutomations(db, firestore)
            } catch (e: Exception) {}
            try { firestore.salva("debiti", fin.id, fin) } catch (e: Exception) {}
        }
    }

    fun deleteFinancing(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allFins = try { db.financingQueries.all().awaitAsList() } catch(e: Exception) { financings.value }
                val fin = allFins.find { it.id == id } ?: return@launch
                if (fin.isDeleted) return@launch

                val deletedFin = FinancingEntity(
                    fin.id, fin.name, fin.creditorName, fin.purpose,
                    fin.installmentAmountCents, fin.commissionCents, fin.extraInstallmentCents, fin.extraInstallmentMonth,
                    fin.totalAmountCents, fin.remainingAmountCents, fin.accountId, fin.categoryId,
                    fin.startEpochDay, fin.endEpochDay, fin.autoGenerateOnReminder, true,
                    true, fin.logoEmoji, fin.logoUri, fin.createdAtEpochMs, now
                )

                db.financingQueries.upsert(
                    deletedFin.id, deletedFin.name, deletedFin.creditorName, deletedFin.purpose,
                    deletedFin.installmentAmountCents, deletedFin.commissionCents, deletedFin.extraInstallmentCents, deletedFin.extraInstallmentMonth,
                    deletedFin.totalAmountCents, deletedFin.remainingAmountCents, deletedFin.accountId, deletedFin.categoryId,
                    deletedFin.startEpochDay, deletedFin.endEpochDay, deletedFin.autoGenerateOnReminder, deletedFin.isClosed,
                    deletedFin.isDeleted, deletedFin.logoEmoji, deletedFin.logoUri, deletedFin.createdAtEpochMs, deletedFin.updatedAtEpochMs
                )

                try { firestore.salva("debiti", id, deletedFin) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun saveRecurring(item: RecurringExpenseEntity) {
        viewModelScope.launch {
            try {
                db.recurringExpenseQueries.upsert(
                    item.id, item.name, item.type, item.amountCents, item.accountId, item.toAccountId,
                    item.categoryId, item.dayOfMonth, item.isEnabled, item.isDeleted, item.cycleTarget,
                    item.logoEmoji, item.logoUri, item.notes, item.autoGenerateOnReminder, item.nextEpochDay,
                    item.frequency, item.isPaused, item.startEpochDay, item.createdAtEpochMs,
                    Clock.System.now().toEpochMilliseconds()
                )

                val todayEpoch = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong()

                // 🚀 FIX: Se l'utente ordina "Applica per questo mese" (data di scadenza <= oggi),
                // scavalchiamo l'AutomationEngine pigro e forziamo noi la prima rata all'istante!
                if (item.nextEpochDay <= todayEpoch && item.isEnabled && !item.isDeleted && item.autoGenerateOnReminder) {
                    payRecurringEarly(item)
                } else {
                    AutomationEngine.processPendingAutomations(db, firestore)
                }
            } catch (e: Exception) {}

            try { firestore.salva("ricorrenti", item.id, item) } catch (e: Exception) {}
        }
    }

    fun deleteRecurring(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allRecs = try { db.recurringExpenseQueries.all().awaitAsList() } catch(e: Exception) { recurringItems.value }
                val rec = allRecs.find { it.id == id } ?: return@launch
                if (rec.isDeleted) return@launch
                val deletedRec = RecurringExpenseEntity(rec.id, rec.name, rec.type, rec.amountCents, rec.accountId, rec.toAccountId, rec.categoryId, rec.dayOfMonth, rec.isEnabled, true, rec.cycleTarget, rec.logoEmoji, rec.logoUri, rec.notes, rec.autoGenerateOnReminder, rec.nextEpochDay, rec.frequency, rec.isPaused, rec.startEpochDay, rec.createdAtEpochMs, now)

                try { db.recurringExpenseQueries.upsert(deletedRec.id, deletedRec.name, deletedRec.type, deletedRec.amountCents, deletedRec.accountId, deletedRec.toAccountId, deletedRec.categoryId, deletedRec.dayOfMonth, deletedRec.isEnabled, deletedRec.isDeleted, deletedRec.cycleTarget, deletedRec.logoEmoji, deletedRec.logoUri, deletedRec.notes, deletedRec.autoGenerateOnReminder, deletedRec.nextEpochDay, deletedRec.frequency, deletedRec.isPaused, deletedRec.startEpochDay, deletedRec.createdAtEpochMs, deletedRec.updatedAtEpochMs) } catch (e: Exception) {}
                try { firestore.salva("ricorrenti", id, deletedRec) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun deleteAccount(accountId: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allAccs = try { db.accountQueries.getAllAccounts().awaitAsList() } catch(e: Exception) { accounts.value }
                val acc = allAccs.find { it.id == accountId } ?: return@launch
                if (acc.isDeleted) return@launch
                val deletedAcc = AccountEntity(acc.id, acc.name, acc.logoEmoji, acc.logoUri, acc.isVisible, acc.isHidden, acc.isSavings, acc.isArchived, true, acc.createdAtEpochMs, now)

                try { db.accountQueries.upsert(deletedAcc.id, deletedAcc.name, deletedAcc.logoEmoji, deletedAcc.logoUri, deletedAcc.isVisible, deletedAcc.isHidden, deletedAcc.isSavings, deletedAcc.isArchived, deletedAcc.isDeleted, deletedAcc.createdAtEpochMs, deletedAcc.updatedAtEpochMs) } catch (e: Exception) {}
                try { firestore.salva("conti", accountId, deletedAcc) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun deleteBudget(categoryId: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allB = try { db.budgetQueries.all().awaitAsList() } catch(e: Exception) { budgets.value }
                val b = allB.find { it.categoryId == categoryId } ?: return@launch
                if (b.isDeleted) return@launch
                val deletedB = BudgetEntity(b.categoryId, b.limitCents, true, b.createdAtEpochMs, now)

                try { db.budgetQueries.upsert(deletedB.categoryId, deletedB.limitCents, deletedB.isDeleted, deletedB.createdAtEpochMs, deletedB.updatedAtEpochMs) } catch (e: Exception) {}
                try { firestore.salva("budgets", categoryId, deletedB) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun deleteFavorite(id: String) {
        viewModelScope.launch {
            val now = Clock.System.now().toEpochMilliseconds()
            try {
                val allF = try { db.favoriteTemplateQueries.all().awaitAsList() } catch(e: Exception) { favorites.value }
                val f = allF.find { it.id == id } ?: return@launch
                if (f.isDeleted) return@launch
                val deletedF = FavoriteTemplateEntity(f.id, f.description, f.amountCents, f.categoryId, f.logoEmoji, f.logoUri, true, f.createdAtEpochMs, now)

                try { db.favoriteTemplateQueries.upsert(deletedF.id, deletedF.description, deletedF.amountCents, deletedF.categoryId, deletedF.logoEmoji, deletedF.logoUri, deletedF.isDeleted, deletedF.createdAtEpochMs, deletedF.updatedAtEpochMs) } catch (e: Exception) {}
                try { firestore.salva("preferiti", id, deletedF) } catch (e: Exception) {}
            } catch (e: Exception) {}
        }
    }

    fun updateRecurring(item: RecurringExpenseEntity) {
        viewModelScope.launch {
            db.recurringExpenseQueries.upsert(
                id = item.id,
                name = item.name,
                type = item.type,
                amountCents = item.amountCents,
                accountId = item.accountId,
                toAccountId = item.toAccountId,
                categoryId = item.categoryId,
                dayOfMonth = item.dayOfMonth,
                isEnabled = item.isEnabled,
                isDeleted = item.isDeleted,
                cycleTarget = item.cycleTarget,
                logoEmoji = item.logoEmoji,
                logoUri = item.logoUri,
                notes = item.notes,
                autoGenerateOnReminder = item.autoGenerateOnReminder,
                nextEpochDay = item.nextEpochDay,
                frequency = item.frequency,
                isPaused = item.isPaused,
                startEpochDay = item.startEpochDay,
                createdAtEpochMs = item.createdAtEpochMs,
                updatedAtEpochMs = item.updatedAtEpochMs
            )

            try {
                firestore.salva("ricorrenti", item.id, item)
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun saveBankAdjustment(cents: Long) {
        _bankAdjustmentCents.value = cents
        viewModelScope.launch {
            val uid = firestore.sessionUid ?: Firebase.auth.currentUser?.uid ?: return@launch
            try {
                val docRef = Firebase.firestore.collection("users").document(uid)
                docRef.set(mapOf("bankAdjustmentCents" to cents), merge = true)
            } catch (e: Exception) {
                println("🔥 MONETIX_DEBUG: [ERRORE UPLOAD] ${e.message}")
            }
            try {
                firestore.salvaBankAdjustment(cents)
            } catch (e: Exception) {
                println("🔥 MONETIX_DEBUG: [ERRORE UPLOAD] Sincronizzazione cloud fallita: ${e.message}")
            }
        }
    }

    // ============================================================================
    // 🚀 MOTORE ESCLUSIVO CARTELLE ESATTORIALI (Aggiunto per Paga e Annulla)
    // ============================================================================
    private fun updateCartellaNotes(notes: String?, isPayment: Boolean): Pair<String?, Long?> {
        if (notes == null) return Pair(null, null)

        val cartellaRegex = Regex("\\[(?:CART|TAX):([^\\]]*)\\]", RegexOption.IGNORE_CASE)
        val match = cartellaRegex.find(notes) ?: return Pair(notes, null)

        val ratesStr = match.groupValues[1]
        val rates = if (ratesStr.isNotBlank()) ratesStr.split(",").map { it.trim() }.toMutableList() else mutableListOf()
        var nextAmount: Long? = null
        var isFinished = false

        if (isPayment) {
            isFinished = true
            for (i in rates.indices) {
                if (!rates[i].endsWith(":P", ignoreCase = true) && !rates[i].endsWith(":p") && !rates[i].endsWith(":X", ignoreCase = true) && !rates[i].endsWith(":x")) {
                    rates[i] = rates[i] + ":P"
                    val nextIndex = i + 1
                    if (nextIndex < rates.size) {
                        nextAmount = rates[nextIndex].replace(Regex(":P", RegexOption.IGNORE_CASE), "").trim().toLongOrNull()
                        isFinished = false
                    }
                    break
                }
            }
        } else {
            for (i in rates.indices.reversed()) {
                if (rates[i].endsWith(":P", ignoreCase = true) || rates[i].endsWith(":p")) {
                    rates[i] = rates[i].replace(Regex(":P", RegexOption.IGNORE_CASE), "").trim()
                    nextAmount = rates[i].toLongOrNull()
                    break
                }
            }
        }

        val newCartList = rates.joinToString(",")
        val tagType = if (match.value.startsWith("[TAX", ignoreCase = true)) "TAX" else "CART"
        var newNotes = notes.replace(match.value, "[$tagType:$newCartList]")

        if (isPayment && isFinished && !newNotes.contains("[ARCHIVED:true]", ignoreCase = true)) {
            newNotes = "$newNotes [ARCHIVED:true]"
        }

        return Pair(newNotes, nextAmount)
    }

    // ============================================================================
    // 🚀 AZIONI DI PAGAMENTO (Singola Transazione TRANSFER)
    // ============================================================================

    fun payRecurringEarly(item: RecurringExpenseEntity) {
        if (!processingSet.add(item.id)) return
        viewModelScope.launch {
            try {
                val allRecs = db.recurringExpenseQueries.all().awaitAsList()
                val latestItem = allRecs.find { it.id == item.id } ?: item

                val allAccs = accounts.value.ifEmpty { db.accountQueries.getAllAccounts().awaitAsList() }.filter { !it.isDeleted }
                val fallbackAccountId = allAccs.firstOrNull()?.id ?: ""
                val sourceAccountId = if (latestItem.accountId.isNullOrBlank() || latestItem.accountId == "null") fallbackAccountId else latestItem.accountId!!

                if (sourceAccountId.isBlank()) return@launch

                val nowMs = Clock.System.now().toEpochMilliseconds()
                val txDate = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong()

                val actualToAccount = if (latestItem.toAccountId == "null" || latestItem.toAccountId.isNullOrBlank()) null else latestItem.toAccountId
                val isTransfer = latestItem.type == "TRANSFER" || actualToAccount != null
                val absoluteAmount = kotlin.math.abs(latestItem.amountCents)

                val originalNotes = latestItem.notes ?: ""
                val recTag = "[rec:${latestItem.id}]"
                val mergedKey = if (originalNotes.contains(recTag)) originalNotes else "$originalNotes $recTag".trim()

                // 🚀 FIX 1: Aggiungiamo [manual] così il Grande Fratello del weekend NON la sposta a Lunedì 14!
                val finalMerchantKey = "$mergedKey [manual]".trim()

                val transaction = TransactionEntity(
                    id = "tx_${nowMs}",
                    accountId = sourceAccountId,
                    toAccountId = if (isTransfer) actualToAccount else null,
                    fromAccountId = if (isTransfer) sourceAccountId else null,
                    amountCents = absoluteAmount,
                    type = if (isTransfer) "TRANSFER" else latestItem.type,
                    dateEpochDay = txDate, // Resta a oggi senza subire slittamenti
                    categoryId = latestItem.categoryId ?: "default",
                    description = latestItem.name ?: "Operazione",
                    merchantKey = finalMerchantKey,
                    subcategoryText = if (isTransfer) "Giroconto" else null,
                    logoEmoji = latestItem.logoEmoji,
                    logoUri = latestItem.logoUri,
                    isHidden = false,
                    isDeleted = false,
                    reminderDays = -1L,
                    createdAtEpochMs = nowMs,
                    updatedAtEpochMs = nowMs
                )

                db.transactionQueries.upsert(
                    transaction.id, transaction.accountId, transaction.toAccountId, transaction.fromAccountId,
                    transaction.amountCents, transaction.type, transaction.dateEpochDay, transaction.categoryId,
                    transaction.description, transaction.merchantKey, transaction.subcategoryText,
                    transaction.logoEmoji, transaction.logoUri, transaction.isHidden, transaction.isDeleted,
                    transaction.reminderDays, transaction.createdAtEpochMs, transaction.updatedAtEpochMs
                )

                try { firestore.salva("movimenti", transaction.id, transaction) } catch(e: Exception) {}

                // Gestione stato di pagamento standard
                val baseUpdatedRec = PaymentEngine.processPaymentState(latestItem, nowMs)

                // 🚀 FIX: Applichiamo la data "nuova" calcolata correttamente da PaymentEngine 
                // in base al frequency, così evitiamo che la logica interna di HomeViewModel 
                // faccia scavallare la data di altri +1 mesi inutilmente!
                // Applica la logica Cartelle se necessario
                val cartellaUpdate = updateCartellaNotes(latestItem.notes, isPayment = true)
                val updatedNotes = cartellaUpdate.first
                val nextAmountCents = cartellaUpdate.second ?: baseUpdatedRec.amountCents
                val isFinished = cartellaUpdate.first?.contains("[ARCHIVED:true]", ignoreCase = true) == true

                val finalUpdatedRec = baseUpdatedRec.copy(
                    startEpochDay = if ((latestItem.cycleTarget ?: 0) > 0) latestItem.startEpochDay else baseUpdatedRec.startEpochDay,
                    nextEpochDay = baseUpdatedRec.nextEpochDay,
                    notes = updatedNotes,
                    amountCents = nextAmountCents,
                    isEnabled = if (isFinished) false else baseUpdatedRec.isEnabled,
                    updatedAtEpochMs = nowMs
                )

                db.recurringExpenseQueries.upsert(
                    finalUpdatedRec.id, finalUpdatedRec.name, finalUpdatedRec.type, finalUpdatedRec.amountCents, finalUpdatedRec.accountId, finalUpdatedRec.toAccountId, finalUpdatedRec.categoryId, finalUpdatedRec.dayOfMonth, finalUpdatedRec.isEnabled, finalUpdatedRec.isDeleted, finalUpdatedRec.cycleTarget, finalUpdatedRec.logoEmoji, finalUpdatedRec.logoUri, finalUpdatedRec.notes, finalUpdatedRec.autoGenerateOnReminder, finalUpdatedRec.nextEpochDay, finalUpdatedRec.frequency, finalUpdatedRec.isPaused, finalUpdatedRec.startEpochDay, finalUpdatedRec.createdAtEpochMs, finalUpdatedRec.updatedAtEpochMs
                )

                try { firestore.salva("ricorrenti", finalUpdatedRec.id, finalUpdatedRec) } catch(e: Exception) {}

            } finally {
                processingSet.remove(item.id)
            }
        }
    }

    fun undoRecurringPayment(item: RecurringExpenseEntity) {
        if (!processingSet.add(item.id)) return
        viewModelScope.launch {
            try {
                val allRecs = db.recurringExpenseQueries.all().awaitAsList()
                val latestItem = allRecs.find { it.id == item.id } ?: return@launch

                val allTxs = db.transactionQueries.getAllTransactions().awaitAsList()
                val lastTx = allTxs.filter { !it.isDeleted && (it.merchantKey?.contains("[rec:${latestItem.id}]") == true || it.description?.contains("[rec:${latestItem.id}]") == true) }.maxByOrNull { it.createdAtEpochMs }
                val nowMs = Clock.System.now().toEpochMilliseconds()

                var revertedAmount = kotlin.math.abs(latestItem.amountCents)

                if (lastTx != null) {
                    revertedAmount = kotlin.math.abs(lastTx.amountCents)
                    db.transactionQueries.softDelete(nowMs, lastTx.id)
                    try { firestore.elimina("movimenti", lastTx.id) } catch(e: Exception) {}
                }

                val baseUpdatedRec = PaymentEngine.processUndoState(latestItem, revertedAmount, nowMs)

                // 🚀 FIX: Applichiamo la data "nuova" calcolata correttamente da PaymentEngine 
                // in base al frequency, così evitiamo che la logica interna di HomeViewModel 
                // faccia scavallare la data di altri -1 mesi inutilmente!
                val finalNextEpoch = if (latestItem.frequency == "Mensile" || latestItem.frequency.isNullOrBlank()) {
                    baseUpdatedRec.nextEpochDay
                } else {
                    baseUpdatedRec.nextEpochDay
                }

                // Applica la logica Cartelle in Undo
                val cartellaUpdate = updateCartellaNotes(latestItem.notes, isPayment = false)
                var updatedNotes = cartellaUpdate.first
                updatedNotes = updatedNotes?.replace(" [ARCHIVED:true]", "", ignoreCase = true)
                val prevAmountCents = cartellaUpdate.second ?: baseUpdatedRec.amountCents

                val todayEpoch = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong()
                val shouldAutoGenerate = if (finalNextEpoch <= todayEpoch) false else baseUpdatedRec.autoGenerateOnReminder

                val finalUpdatedRec = baseUpdatedRec.copy(
                    nextEpochDay = finalNextEpoch,
                    notes = updatedNotes,
                    amountCents = prevAmountCents,
                    isEnabled = true,
                    autoGenerateOnReminder = shouldAutoGenerate,
                    updatedAtEpochMs = nowMs
                )

                db.recurringExpenseQueries.upsert(
                    finalUpdatedRec.id, finalUpdatedRec.name, finalUpdatedRec.type, finalUpdatedRec.amountCents, finalUpdatedRec.accountId, finalUpdatedRec.toAccountId, finalUpdatedRec.categoryId, finalUpdatedRec.dayOfMonth, finalUpdatedRec.isEnabled, finalUpdatedRec.isDeleted, finalUpdatedRec.cycleTarget, finalUpdatedRec.logoEmoji, finalUpdatedRec.logoUri, finalUpdatedRec.notes, finalUpdatedRec.autoGenerateOnReminder, finalUpdatedRec.nextEpochDay, finalUpdatedRec.frequency, finalUpdatedRec.isPaused, finalUpdatedRec.startEpochDay, finalUpdatedRec.createdAtEpochMs, finalUpdatedRec.updatedAtEpochMs
                )

                try { firestore.salva("ricorrenti", finalUpdatedRec.id, finalUpdatedRec) } catch(e: Exception) {}

            } finally {
                processingSet.remove(item.id)
            }
        }
    }
    // 🚀 FORZA IL CONTROLLO DEI PAC USANDO IL SALDO REALE DEL CONTO ATTIVO
    fun forcePacCheck(onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            try {
                val allGoalsBefore = db.savingsGoalQueries.all().awaitAsList()
                val totalBefore = allGoalsBefore.filter { !it.isDeleted && it.name.contains("[PAC:") }.sumOf { it.savedAmountCents }

                // 🚀 NON PASSIAMO IL SALDO FORZATO: il motore lo calcola separato per conto nella mappa
                AutomationEngine.processPendingAutomations(db, firestore)

                kotlinx.coroutines.delay(1000)

                val allGoalsAfter = db.savingsGoalQueries.all().awaitAsList()
                val totalAfter = allGoalsAfter.filter { !it.isDeleted && it.name.contains("[PAC:") }.sumOf { it.savedAmountCents }

                onResult(totalAfter > totalBefore)
            } catch (e: Exception) {
                onResult(false)
            }
        }
    }

    private fun addMonths(date: LocalDate, monthsToAdd: Int, preferredDay: Int = date.dayOfMonth): LocalDate {
        var m = date.monthNumber + monthsToAdd
        var y = date.year
        while (m > 12) { m -= 12; y++ }
        return LocalDate(y, m, minOf(preferredDay, lengthOfMonthKmp(y, m)))
    }

    private fun lengthOfMonthKmp(year: Int, month: Int): Int {
        val isLeap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0))
        return when (month) { 2 -> if (isLeap) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
    }
}